import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { OrganizationProvider } from './contexts/OrganizationContext';
import { ModalProvider } from './contexts/ModalContext';
import { useTokenExpiration } from './hooks/useTokenExpiration';
import Layout from './components/Layout/Layout';
import LoginForm from './components/Auth/LoginForm';
import NotificationContainer from './components/NotificationContainer';
import HomePage from './pages/HomePage';
import LandingPage from './pages/LandingPage';
import DefinitionsPage from './pages/DefinitionsPage';
import AddDefinitionPage from './pages/AddDefinitionPage';

import DataBuildsPage from './pages/DataBuildsPage';
import DataBuildDetailsPage from './pages/DataBuildDetailsPage';
import DefinitionDetailsPage from './pages/DefinitionDetailsPage';
import NewBuildPage from './pages/NewBuildPage';
import EnvironmentsPage from './pages/EnvironmentsPage';
import EnvironmentDetailsPage from './pages/EnvironmentDetailsPage';
import DataComparisonPage from './pages/DataComparisonPage';
import BundleComparisonPage from './pages/BundleComparisonPage';
import AIChatPage from './pages/AIChatPage';
import DataSourcesPage from './pages/DataSourcesPage';
import BookDemoPage from './pages/BookDemoForm';

const AppRoutes: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();
  
  // Use the token expiration hook
  useTokenExpiration();

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginForm />} />
        <Route path="/book-demo" element={<BookDemoPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Navigate to="/home" replace />} />
        <Route path="/home" element={<HomePage />} />
        <Route path="/definitions" element={<DefinitionsPage />} />
        <Route path="/definitions/add" element={<AddDefinitionPage />} />

        <Route path="/definitions/:id" element={<DefinitionDetailsPage />} />
        <Route path="/data-builds" element={<DataBuildsPage />} />
        <Route path="/data-builds/:id" element={<DataBuildDetailsPage />} />
        <Route path="/new-build" element={<NewBuildPage />} />
        <Route path="/environments" element={<EnvironmentsPage />} />
        <Route path="/environments/:envName" element={<EnvironmentDetailsPage />} />
        <Route path="/data-comparison" element={<DataComparisonPage />} />
        <Route path="/bundle-comparison" element={<BundleComparisonPage />} />
        <Route path="/data-sources" element={<DataSourcesPage />} />
        <Route path="/ai-chat" element={<AIChatPage />} />
        <Route path="/book-demo" element={<BookDemoPage />} />
        <Route path="*" element={<Navigate to="/home" replace />} />
      </Routes>
    </Layout>
  );
};

const App: React.FC = () => {
  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <NotificationProvider>
        <AuthProvider>
          <OrganizationProvider>
            <ModalProvider>
              <AppRoutes />
              <NotificationContainer />
            </ModalProvider>
          </OrganizationProvider>
        </AuthProvider>
      </NotificationProvider>
    </Router>
  );
};

export default App;
